import ArrowIcon from 'assets/svg/arrow/arrow.svg'
import Flex from 'common/components/Flex'
import HeaderLogo from 'common/components/Header/HeaderLogo'
import HeaderUserButtonGroup from 'common/components/Header/HeaderUserButtonGroup'
import BusinessSolutionsPopover from 'common/components/Header/RevampHeader/BusinessSolutionsPopover'
import PerusahaanPopover from 'common/components/Header/RevampHeader/PerusahaanPopover'
import Loader from 'common/components/Loader'
import Overlay from 'common/components/Overlay'
import Popover from 'common/components/Popover'
import SearchBar from 'common/components/SearchBar/Revamp'
import TopNav from 'common/components/TopNav/Revamp'
import Typography from 'common/components/Typography'
import {
  ACTIVE_THEME_NAME,
  WEB_DESKTOP_RALALI_URL,
} from 'common/constants'
import theme from 'common/theme'
import { getCookie } from 'lib/cookie'
import { useFeatureFlags } from 'lib/feature-flags'
import ThemeColorHelper from 'lib/theme-color-helper'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useStoreActions, useStoreState } from 'stores/hooks'
import styled from 'styled-components'

const ModalSuspendedRalaliPlus = dynamic(
  () =>
    import('services/RalaliPlus/widgets/ModalSuspendedRalaliPlus'),
  { ssr: false },
)

const Container = styled.div`
  background-color: ${ThemeColorHelper('white')};
  background-image: ${theme.backgroundImage[ACTIVE_THEME_NAME]};
  background-position: top;
  background-repeat: no-repeat;
  background-size: 100%;
  position: sticky;
  top: 0;
  z-index: ${theme.zIndex.header};
  ${({ hideTopNav }) =>
    hideTopNav &&
    `
    box-shadow: 0 2px 7px 0 rgba(0, 0, 0, 0.08);
  `}
`

const SearchBarWrapper = styled.div`
  width: ${theme.layout.containerWidth};
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 auto;
  padding: 20px 0;
`

const StyledSearchBar = styled(SearchBar)`
  margin-left: 30px;
  margin-right: 14px;
  input {
    border-radius: 25px 0 0 25px;
    background-color: #efefef;
  }
  input: focus {
    border: solid 1px white;
  }
  > div > * + div {
    background-color: #efefef;
    border: 1px solid #efefef;
    border-left: none;
    border-radius: 0 25px 25px 0;

    > div > * > path {
      fill: black;
    }
  }
`

const LoaderWrapper = styled(Flex)`
  height: 100vh;
`
const MenuMain = styled.ul`
  padding: 0;
  margin: 0;
  list-style: none;
  display: flex;
  flex-direction: row;
  height: 18px;
  align-items: center;
  li {
    margin: 0 30px 0 0;
    position: relative;
    :last-child {
      margin: 0 30px 0 0;
    }
  }
`

const StyledLink = styled.a`
  display: flex;
  align-items: center;
  cursor: pointer;
  color: ${theme.colors.black};
  &:hover,
  &:focus {
    color: ${theme.colors.orange};
    > p {
      color: ${theme.colors.orange};
    }
  }
  > p {
    text-transform: capitalize;
    white-space: nowrap;
  }
`

const BottomArrowIcon = styled(ArrowIcon)`
  transform-origin: 50% 50%;
  transform: scale(0.8) rotate(180deg);
  margin-left: 2px;
  path {
    fill: currentColor;
  }
`

const WrapperTooltip = styled.span`
  position: absolute;
  top: -14px;
  right: ${({ hasPopover }) => (hasPopover ? '9px' : '-12px')};
`

function Header({
  hideTopNav = false,
  lang,
}: {
  hideTopNav?: boolean
  lang?: string
}): JSX.Element {
  const { t } = useTranslation('common')

  // FEATURE FLAGS
  const isFeatureSetAndChangePinEnable = useFeatureFlags(
    'biz--ralali-pin--enabled-set-and-change-pin--global',
  )

  const isMarketplaceCartEnabled = useFeatureFlags(
    'biz--marketplace-enable-cart--global',
  )

  const { isLoading: isLoadingLogout } = useStoreState(
    (actions) => actions.logOut,
  )
  const {
    isLoading: isLoadingRalaliPlus,
    balanceData,
  } = useStoreState((state) => state.ralaliPlus)
  const { getTotalUnreadMessages } = useStoreActions(
    (actions) => actions.userChat,
  )
  const isLogin =
    !!getCookie('access_token') || !!getCookie('refresh_token')
  const [isOpenOverlay, setIsOpenOverlay] = useState<boolean>(false)
  const handleOpenOverlay = () => {
    setIsOpenOverlay(true)
  }

  const handleCloseOverlay = () => {
    setIsOpenOverlay(false)
  }

  const [isOpenRalaliPlusModal, setIsOpenRalaliPlusModal] = useState(
    false,
  )
  const [
    isLoadedRalaliPlusModal,
    setIsLoadedRalaliPlusModal,
  ] = useState(false)

  const [
    isLoadedSuspendedRalaliPlusModal,
    setIsLoadedSuspendedRalaliPlusModal,
  ] = useState(false)

  const handleOpenRalaliPlusModal = () => {
    if (
      !isLoadedRalaliPlusModal &&
      !isLoadingRalaliPlus &&
      balanceData?.status === 'suspended'
    ) {
      setIsLoadedSuspendedRalaliPlusModal(true)
    } else {
      setIsLoadedRalaliPlusModal(true)
    }
    setIsOpenRalaliPlusModal(!isOpenRalaliPlusModal)
  }

  useEffect(() => {
    getTotalUnreadMessages('marketplace')
  }, [])

  return (
    <>
      <Overlay
        isActive={isOpenOverlay}
        onOverlayClick={handleCloseOverlay}
        zIndex="6"
      />
      {isLoadingLogout && (
        <Overlay
          zIndex={`${theme.zIndex.modal}`}
          isActive={isLoadingLogout}
        >
          <LoaderWrapper
            data-testid="loading-logout-overlay"
            alignItems="center"
            justifyContent="center"
          >
            <Loader />
          </LoaderWrapper>
        </Overlay>
      )}
      <Container data-testid="header" hideTopNav={hideTopNav}>
        <SearchBarWrapper>
          <Link href={`${WEB_DESKTOP_RALALI_URL}`}>
            <a>
              <HeaderLogo />
            </a>
          </Link>

          {/* TODO:: replace next line with Search Component */}
          <StyledSearchBar
            // lastSeenItems={lastSeenDummy}
            // popularCategories={lastSeenDummy}
            onOpenOverlay={handleOpenOverlay}
            onCloseOverlay={handleCloseOverlay}
            width="100%"
          />

          <MenuMain>
            <li>
              <>
                <Popover
                  side="bottom"
                  position="right"
                  onOpenOverlay={handleOpenOverlay}
                  onCloseOverlay={handleCloseOverlay}
                  full
                  content={<PerusahaanPopover />}
                >
                  <StyledLink data-testid="services-menu-test">
                    <Typography type="rparagraph">
                      {t('COMPANY')}
                    </Typography>
                    <BottomArrowIcon />
                  </StyledLink>
                </Popover>
              </>
            </li>
            <li>
              <StyledLink href={`${WEB_DESKTOP_RALALI_URL}/food`}>
                <WrapperTooltip>
                  {/* <NewTooltipIcon /> */}
                </WrapperTooltip>
                <Typography type="bparagraph" color="orange">
                  Ralalifood
                </Typography>
              </StyledLink>
            </li>
            <li>
              <>
                <Popover
                  side="bottom"
                  position="right"
                  onOpenOverlay={handleOpenOverlay}
                  onCloseOverlay={handleCloseOverlay}
                  full
                  content={<BusinessSolutionsPopover />}
                >
                  <StyledLink data-testid="services-menu-test">
                    <Typography type="rparagraph">
                      {t('BUSINESS_SOLUTIONS')}
                    </Typography>
                    <BottomArrowIcon />
                  </StyledLink>
                </Popover>
                <WrapperTooltip hasPopover>
                  {/* <NewTooltipIcon /> */}
                </WrapperTooltip>
              </>
            </li>
            <li>
              <StyledLink
                href={`${WEB_DESKTOP_RALALI_URL}/hubungikami`}
              >
                <Typography type="rparagraph">
                  {t('CONTACT_US')}
                </Typography>
              </StyledLink>
            </li>
          </MenuMain>

          <HeaderUserButtonGroup
            onCloseOverlay={handleCloseOverlay}
            onOpenOverlay={handleOpenOverlay}
            onOpenRalaliPlusModal={handleOpenRalaliPlusModal}
            isFeatureSetAndChangePinEnable={
              isFeatureSetAndChangePinEnable
            }
            isLogin={isLogin}
            isMarketplaceCartEnabled={isMarketplaceCartEnabled}
          />
        </SearchBarWrapper>

        <TopNav
          onOpenOverlay={handleOpenOverlay}
          onCloseOverlay={handleCloseOverlay}
          show={!hideTopNav}
          lang={lang}
        />
      </Container>
      {isLoadedSuspendedRalaliPlusModal ? (
        <ModalSuspendedRalaliPlus
          isOpen={isOpenRalaliPlusModal}
          onClose={handleOpenRalaliPlusModal}
        />
      ) : (
        ''
      )}
    </>
  )
}

export default Header
